import LaunchIcon from '@mui/icons-material/Launch';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { Box, IconButton, Link, Skeleton, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { produce } from 'immer';
import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ReactComponent as LinkedInIcon } from '../../../../../assets/icons/linkedin.svg';
import { DashboardsContext } from '../../../../../contexts/DashboardsContext';
import { getDashboardQueriesFunc } from '../../../../../lib/helper';
import theme from '../../../../../theme';
import { Dashboard, DashboardQuery } from '../../../../../types/files';
import KeyPeopleModal from '../../../../modals/tables/dashboards/rows/KeyPeopleModal';
import useStyles from "../styles";

const KeyPeopleCell: React.FC<{ dashboard: Dashboard }> = ({ dashboard }) => {
    const classes = useStyles();
    const { mappedOverviewQueries, setDashboardsQueries } = useContext(DashboardsContext);

    const [keyPeopleLoaded, setKeyPeopleLoaded] = useState<boolean>(false);
    const [keyPeople, setKeyPeople] = useState<string>('');
    const [keyPeopleModalOpen, setKeyPeopleModalOpen] = useState<boolean>(false);
    const [fieldHover, setFieldHover] = useState<boolean>(false);
    const keyPeopleRef = useRef<HTMLElement>(null);

    const queryKey = useMemo(() => `${dashboard.id}:Key People`, [dashboard.id]);

    const getRoleRank = useCallback((role: string): number => {
        const roleUpper = role?.toUpperCase() || '';

        // Rank 1 – Founder (ownership) / CEO (highest executive)
        if (roleUpper.includes('FOUNDER') || roleUpper.includes('CEO')) return 1;
        // Rank 2 – President (if separate from CEO)
        if (roleUpper.includes('PRESIDENT') && !roleUpper.includes('VICE')) return 2;
        // Rank 3 – All C-suite (COO, CFO, CTO, CMO, CHRO, etc.)
        if (roleUpper.includes('CHIEF') ||
            (roleUpper.includes('OFFICER') && (roleUpper.includes('CFO') || roleUpper.includes('COO') || roleUpper.includes('CTO') || roleUpper.includes('CMO') || roleUpper.includes('CPO') || roleUpper.includes('CRO') || roleUpper.includes('CHRO') || roleUpper.includes('CDO') || roleUpper.includes('CIO') || roleUpper.includes('CSO') || roleUpper.includes('CCO'))) ||
            /^C[A-Z]O\b/.test(roleUpper)) return 3;
        // Rank 4 – Executive VP / Senior VP
        if ((roleUpper.includes('EXECUTIVE') && roleUpper.includes('VP')) ||
            (roleUpper.includes('SENIOR') && roleUpper.includes('VP')) ||
            (roleUpper.includes('EXECUTIVE') && roleUpper.includes('VICE PRESIDENT')) ||
            (roleUpper.includes('SENIOR') && roleUpper.includes('VICE PRESIDENT'))) return 4;
        // Rank 5 – VP
        if (roleUpper.includes('VICE PRESIDENT') || roleUpper.includes('VP')) return 5;
        // Rank 6 – Head of / Director
        if (roleUpper.includes('HEAD OF') || roleUpper.includes('DIRECTOR')) return 6;
        // Rank 7 – Senior Managers / Senior Specialists
        if (roleUpper.includes('SENIOR')) return 7;
        // Rank 8 – Mid-level & Junior roles
        if (roleUpper.includes('MANAGER') || roleUpper.includes('LEAD') || roleUpper.includes('SPECIALIST') ||
            roleUpper.includes('ANALYST') || roleUpper.includes('COORDINATOR') || roleUpper.includes('JUNIOR')) return 8;
        // Rank 9 – Administrative & Support staff
        if (roleUpper.includes('SECRETARY') || roleUpper.includes('ASSISTANT') || roleUpper.includes('CLERK') ||
            roleUpper.includes('ADMIN') || roleUpper.includes('SUPPORT')) return 9;

        // All others (default)
        return 10;
    }, []);

    const sortedKeyPeople = useMemo(() => {
        if (Array.isArray(keyPeople)) {
            return keyPeople.sort((a: any, b: any) => {
                const rankA = getRoleRank(a.role);
                const rankB = getRoleRank(b.role);

                if (rankA !== rankB)
                    return rankA - rankB;

                if (a.role < b.role) return -1;
                if (a.role > b.role) return 1;

                return 0;
            });
        }

        return keyPeople;
    }, [getRoleRank, keyPeople]);

    useQuery({
        queryKey: ['dashboardQuery', `${dashboard.id}:Key People`],
        queryFn: async () => !mappedOverviewQueries.has(queryKey)
            ? await getDashboardQueriesFunc(dashboard.id, 'Key People') : [],
        staleTime: 5 * 60 * 1000,
        retry: 2,
        onSuccess: (bulkQueryData: DashboardQuery[]) => {
            setDashboardsQueries(prev => produce(prev, draft => {
                bulkQueryData.forEach(newQuery => {
                    if (!draft.find(query => query.dashboardId === newQuery.dashboardId && query.title === newQuery.title))
                        draft.push(newQuery);
                });
            }));
            setKeyPeopleLoaded(true);
        },
    });

    useEffect(() => {
        if (mappedOverviewQueries.has(queryKey)) {
            setKeyPeople(mappedOverviewQueries.get(queryKey) || '');
            setKeyPeopleLoaded(true);
        }
    // eslint-disable-next-line
    }, [mappedOverviewQueries]);

    return (<>
        <Stack width="100%" ref={keyPeopleRef} onMouseLeave={() => setFieldHover(false)}>
            {keyPeopleLoaded ? (Array.isArray(sortedKeyPeople) && !!sortedKeyPeople.length ? (<>
                <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%"
                    onMouseEnter={() => setFieldHover(true)}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" width="100%">
                        <Box pt="4px">
                            {!!sortedKeyPeople[0]?.link ? (
                                <LinkedInIcon className={classes.icon} fill={'#7bd4d4'} />
                            ) : (
                                <PersonOutlineOutlinedIcon sx={{ width: 24, height: 24, fill: "rgb(163, 167, 172)" }} />
                            )}
                        </Box>
                        <Link className={classes.person}
                            href={sortedKeyPeople[0]?.link}
                            underline="hover"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ maxWidth: fieldHover ? (sortedKeyPeople.length > 1 ? '9ch' : '18ch') : (sortedKeyPeople.length > 1 ? '12ch' : '20ch') }}>
                            {sortedKeyPeople[0]?.title?.split('|')?.[0]?.split('-')?.[0] || '[Person]'}
                        </Link>
                        <Typography fontFamily="Inter" fontSize="0.85rem" color="#666666">
                            {(sortedKeyPeople.length > 1) && (
                                <span>{` + ${sortedKeyPeople.length - 1} other${sortedKeyPeople.length - 1 > 1 ? 's' : ''}`}</span>
                            )}
                        </Typography>
                    </Stack>
                    {fieldHover && (
                        <Stack direction="row" alignItems="stretch" justifyContent="flex-end">
                            <IconButton disableRipple
                                onClick={() => setKeyPeopleModalOpen(true)}
                                sx={{ padding: 'unset !important'}}>
                                <LaunchIcon className={classes.icon} sx={{ fill: theme.colors.neutral['500'] }} />
                            </IconButton>
                        </Stack>
                    )}
                </Stack>
            </>) : (
                <Typography className={classes.url} color={`${theme.colors.neutral['500']} !important`}>
                    {'No key people found'}
                </Typography>
            )) : (
                <Box sx={{ width: '100%' }}>
                    <Skeleton animation="wave" className={classes.wave} />
                </Box>
            )}
        </Stack>
        {keyPeopleModalOpen && (
            <KeyPeopleModal open={keyPeopleModalOpen}
                dashboard={dashboard}
                anchorElement={keyPeopleRef.current}
                onClose={() => setKeyPeopleModalOpen(false)} />
        )}
    </>);
}

export default memo(KeyPeopleCell);
